<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { ArrowRight } from "@element-plus/icons-vue";
import { OidcClient } from "@/oidc/OidcClient";
import { getToken } from "@/utils/auth";
import { clearCookie } from "@/utils/tools";
import { loginLog } from "@/api/user";
import { getClockInStatus, registration, showContract } from "@/api/layout";
import { useUserStore } from "@/stores/user";
import { usePermissionStore } from "@/stores/permission";
import { useLayoutStore } from "@/stores/layout";
import { useTheme } from "@/composables/useTheme";
import { useConfirm } from "@/composables/useFeedback";

const emit = defineEmits(["close"]);

const router = useRouter();
const userStore = useUserStore();
const permissionStore = usePermissionStore();
const layoutStore = useLayoutStore();
const { isSoftMode, setSoftMode } = useTheme();
const { alert } = useConfirm();

const softMode = ref(false);
const showSoftMode = ref(true);
const clockInStatus = ref(-1);
const isClockClick = ref(true);
const auditStatus = ref("Not signed");
const auditStatusColor = ref("#32397a66");
const showSignContract = ref(false);
const lv = ref("T0");

const clockText = ["Clock In", "Clocked In", "Late"];
const clockClass = ["clock-btn--ready", "clock-btn--in", "clock-btn--late"];

const avatarUrl = computed(() => userStore.avatar || userStore.defaultAvatar);

const stats = computed(() => {
  const items = [
    { name: "Growth Values", key: "growth", dialog: "values" },
    { name: "Enrolment", key: "enrolment", dialog: "enrolment" },
    { name: "My Flowers", key: "flowers", path: "flowers" },
  ];
  if (!userStore.isGrowthSystemTestGroup) {
    return items.filter((i) => i.name !== "Growth Values");
  }
  return items;
});

const menuItems = computed(() => {
  const list = [
    {
      title: "Integral Level",
      icon: "show_chart",
      grade: true,
      dialog: "levels",
    },
    { title: "QA Test Report", icon: "report", dialog: "report" },
    { title: "Change E-mail", icon: "email", dialog: "email" },
    { title: "Flowers For Cash", icon: "loop", dialog: "cashs" },
    { title: "Personal Data", icon: "settings", path: "newPersonalData" },
    { title: "Leave", icon: "event_note", path: "leaveRequest" },
    { title: "Log Out", icon: "power_settings_new", isLoginOut: true },
  ];
  if (showSignContract.value) {
    list.splice(4, 0, {
      title: "Sign Contract",
      icon: "edit",
      path: "signContract",
    });
  }
  return list;
});

function onAvatarError(e) {
  e.target.src = userStore.defaultAvatar;
}

function formatScore(val) {
  const n = Number(val) || 0;
  return n >= 0 ? `+${n}` : String(n);
}

function getStatValue(item) {
  if (item.name === "Growth Values")
    return formatScore(userStore.growthSystemScore);
  if (item.name === "My Flowers") return formatScore(userStore.flowers);
  if (item.name === "Enrolment") {
    // Vue2: Tol{count} / Lv{lv}
    const count = Number(userStore.transformPeriodCount) || 0;
    updateLv(count);
    return `Tol ${count} / Lv ${lv.value}`;
  }
  return "";
}

function updateLv(count) {
  if (count <= 29) lv.value = "T0";
  else if (count <= 49) lv.value = "T1";
  else if (count <= 79) lv.value = "T2";
  else if (count <= 129) lv.value = "T3";
  else if (count <= 209) lv.value = "T4";
  else if (count <= 339) lv.value = "T5";
  else if (count <= 549) lv.value = "T6";
  else if (count <= 879) lv.value = "T7";
  else if (count <= 1439) lv.value = "T8";
  else if (count <= 2329) lv.value = "T9";
  else lv.value = "T10";
}

function comingSoon(name) {
  emit("close");
  ElMessage.info(`${name} — coming soon`);
}

function onStatClick(item) {
  // Vue2 excutePoints → setGrowthValuesDialog / setenrolmentDialog / flowers route
  if (item.dialog === "values") {
    emit("close");
    layoutStore.openGrowthValuesDialog();
    return;
  }
  if (item.dialog === "enrolment") {
    emit("close");
    layoutStore.openEnrolmentDialog();
    return;
  }
  if (item.path) {
    emit("close");
    router.push(`/${item.path}`).catch(() => comingSoon(item.name));
    return;
  }
  comingSoon(item.name);
}

function onMenuClick(item) {
  if (item.isLoginOut) {
    loginOut();
    return;
  }
  if (item.dialog === "cashs") {
    emit("close");
    layoutStore.openCashDialog();
    return;
  }
  if (item.dialog === "levels") {
    emit("close");
    layoutStore.openLevelDialog();
    return;
  }
  if (item.dialog === "report") {
    emit("close");
    layoutStore.openReportDialog();
    return;
  }
  if (item.dialog === "email") {
    emit("close");
    layoutStore.openEmailDialog();
    return;
  }
  if (item.dialog) {
    comingSoon(item.title);
    return;
  }
  if (item.path) {
    emit("close");
    if (item.path === "signContract") {
      handleSignContract();
      return;
    }
    router.push(`/${item.path}`).catch(() => comingSoon(item.title));
  }
}

async function handleSignContract() {
  if (userStore.isNeedToImprovePersonalInformation) {
    // Match Vue2 / 图1: TutorConfirmDialog (pink ! + Confirm), not Notice+OK MessageBox
    const ok = await alert("Please kindly upload your personal information ASAP.", {
      type: "default",
      icon: { default: true },
      width: 420,
      confirmButtonText: "Confirm",
    });
    if (ok) router.push("/newPersonalData").catch(() => {});
    return;
  }
  if (userStore.awaitApproval) {
    ElMessage.info(
      "Your Personal Data is being verified, please confirm with your TM and sign the digital contract once it's done. Thanks",
    );
    return;
  }
  const type = userStore.isInternationalOrCustom;
  if (type === 1)
    router.push("/signContractCustom").catch(() => comingSoon("Sign Contract"));
  else if (type === 2)
    router.push("/signContractEditor").catch(() => comingSoon("Sign Contract"));
  else router.push("/signContract").catch(() => comingSoon("Sign Contract"));
}

async function onClockClick() {
  if (!isClockClick.value) return;
  try {
    const res = await registration();
    if (res.data?.ErrorCode === 0) {
      const status = res.data.Body;
      ElMessage({
        message: res.data.Msg || "OK",
        type: status === 1 ? "success" : "error",
      });
      if (status === 0) isClockClick.value = true;
      else if (status === 1 || status === -1) {
        clockInStatus.value = 1;
        isClockClick.value = false;
      } else if (status === 2 || status === 3) {
        clockInStatus.value = 2;
        isClockClick.value = false;
      } else {
        isClockClick.value = false;
      }
    } else {
      ElMessage.error(res.data?.Msg || "Clock in failed");
    }
  } catch (e) {
    ElMessage.error("Clock in failed");
  }
}

async function onSoftModeChange(val) {
  try {
    await setSoftMode(!!val);
  } catch (e) {
    softMode.value = !val;
    ElMessage.error(e?.message || "Failed to switch Soft Mode");
  }
}

async function loginOut() {
  emit("close");
  try {
    clearCookie("loginSign");
    loginLog().catch(() => {});
    const client = new OidcClient();
    await client.singOut({ id_token_hint: getToken() });
    userStore.fedLogOut();
    permissionStore.reset();
  } catch (err) {
    console.error(err);
    userStore.fedLogOut();
    permissionStore.reset();
    window.location.href = "/welcome";
  }
}

async function loadClockStatus() {
  try {
    const res = await getClockInStatus();
    const status = res.data?.Body;
    if (status == null) return;
    clockInStatus.value = status;
    isClockClick.value = status === 0;
  } catch (_) {
    /* ignore until API ready */
  }
}

async function loadContract() {
  try {
    const res = await showContract();
    if (res.data?.ErrorCode === 0 && res.data.Body?.Item1) {
      showSignContract.value = true;
      userStore.setInternationalOrCustom(res.data.Body.Item3);
      switch (res.data.Body.Item2) {
        case 0:
          auditStatus.value = "Auditing";
          auditStatusColor.value = "#EE344C";
          break;
        case 1:
          auditStatus.value = "Audit Passed";
          auditStatusColor.value = "#3E67FB";
          break;
        case 2:
          auditStatus.value = "Audit failed";
          auditStatusColor.value = "#EE344C";
          break;
        default:
          auditStatus.value = "Not signed";
          auditStatusColor.value = "#32397a66";
      }
    }
  } catch (_) {
    /* ignore */
  }
}

onMounted(() => {
  softMode.value = isSoftMode.value;
  loadClockStatus();
  loadContract();
  // Keep Enrolment Tol in sync (Vue2 GetTutorTrail)
  userStore.fetchTutorTrail();
});

watch(isSoftMode, (v) => {
  softMode.value = v;
});
</script>

<template>
  <div class="person-panel">
    <div class="person-panel__top">
      <img
        :src="avatarUrl"
        alt=""
        width="48"
        height="48"
        @error="onAvatarError"
      />
      <button
        v-if="clockInStatus >= 0"
        type="button"
        class="clock-btn"
        :class="[
          clockClass[clockInStatus],
          isClockClick ? 'is-clickable' : 'is-disabled',
        ]"
        @click="onClockClick"
      >
        {{ clockText[clockInStatus] }}
      </button>
    </div>

    <div v-if="showSoftMode" class="person-panel__soft">
      <el-switch v-model="softMode" @change="onSoftModeChange" />
      <span>Soft Mode</span>
    </div>

    <div class="person-panel__divider" />

    <div class="person-panel__stats">
      <button
        v-for="item in stats"
        :key="item.name"
        type="button"
        class="stat-row"
        @click="onStatClick(item)"
      >
        <span>{{ item.name }}</span>
        <span class="stat-row__value">{{ getStatValue(item) }}</span>
      </button>
    </div>

    <div class="person-panel__divider" />

    <div class="person-panel__menu">
      <button
        v-for="item in menuItems"
        :key="item.title"
        type="button"
        class="menu-row"
        @click="onMenuClick(item)"
      >
        <i class="menu-row__icon" :class="item.icon" />
        <span class="menu-row__title">{{ item.title }}</span>
        <span v-if="item.grade" class="menu-row__extra blue">{{
          userStore.integralLevel || "D1"
        }}</span>
        <span
          v-else-if="item.title === 'Sign Contract'"
          class="menu-row__extra"
          :style="{ color: auditStatusColor }"
          >{{ auditStatus }}</span
        >
        <el-icon class="menu-row__arrow"><ArrowRight /></el-icon>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.person-panel {
  width: 280px;
  padding: 20px 0 12px;
  color: var(--app-text, $blue-title);
  font-family: inherit;
  background: var(--app-surface, #fff);

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;

    img {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  &__soft {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px 8px;
    font-size: 14px;
  }

  &__divider {
    height: 1px;
    background: var(--app-border, rgba(50, 57, 122, 0.08));
    margin: 4px 0;
  }

  &__stats {
    padding: 4px 0 8px;
  }

  &__menu {
    padding: 4px 0 0;
  }
}

.clock-btn {
  border: none;
  height: 24px;
  line-height: 24px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-family: inherit;
  color: #fff;

  &--ready {
    background: $blue-title-2;
  }
  &--in {
    background: #0acc94;
  }
  &--late {
    background: #ee344c;
  }
  &.is-clickable {
    cursor: pointer;
  }
  &.is-disabled {
    cursor: default;
  }
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 16px;
  font-size: 14px;
  font-family: inherit;
  color: var(--app-text, $blue-title);
  cursor: pointer;

  /* 个人面板专用悬停色（含 Soft Mode）；侧栏等用 --app-hover */
  &:hover {
    background: #e9f4ff;
    color: $blue-title;
  }

  &__value {
    color: $blue-title-2;
    font-weight: 600;
  }
}

.menu-row {
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 12px 8px 16px;
  font-size: 14px;
  font-family: inherit;
  color: var(--app-text, $blue-title);
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #e9f4ff;
    color: $blue-title;

    .menu-row__icon.show_chart {
      background-image: url("@/assets/img/common/headerbar/panel-icon1-cur.png");
    }
    .menu-row__icon.report {
      background-image: url("@/assets/img/common/headerbar/panel-icon8-cur.png");
    }
    .menu-row__icon.email {
      background-image: url("@/assets/img/common/headerbar/panel-icon2-cur.png");
    }
    .menu-row__icon.loop {
      background-image: url("@/assets/img/common/headerbar/panel-icon3-cur.png");
    }
    .menu-row__icon.settings {
      background-image: url("@/assets/img/common/headerbar/panel-icon4-cur.png");
    }
    .menu-row__icon.edit {
      background-image: url("@/assets/img/common/headerbar/panel-icon5-cur.png");
    }
    .menu-row__icon.event_note {
      background-image: url("@/assets/img/common/headerbar/panel-icon6-cur.png");
    }
    .menu-row__icon.power_settings_new {
      background-image: url("@/assets/img/common/headerbar/panel-icon7-cur.png");
    }
    .menu-row__arrow {
      color: $blue-title-2;
    }
  }

  &__icon {
    width: 20px;
    height: 24px;
    margin-right: 12px;
    flex-shrink: 0;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;

    &.show_chart {
      background-image: url("@/assets/img/common/headerbar/panel-icon1.png");
    }
    &.report {
      background-image: url("@/assets/img/common/headerbar/panel-icon8.png");
    }
    &.email {
      background-image: url("@/assets/img/common/headerbar/panel-icon2.png");
    }
    &.loop {
      background-image: url("@/assets/img/common/headerbar/panel-icon3.png");
    }
    &.settings {
      background-image: url("@/assets/img/common/headerbar/panel-icon4.png");
    }
    &.edit {
      background-image: url("@/assets/img/common/headerbar/panel-icon5.png");
    }
    &.event_note {
      background-image: url("@/assets/img/common/headerbar/panel-icon6.png");
    }
    &.power_settings_new {
      background-image: url("@/assets/img/common/headerbar/panel-icon7.png");
    }
  }

  &__title {
    flex: 1;
  }

  &__extra {
    margin-right: 4px;
    font-style: italic;
    font-size: 13px;

    &.blue {
      color: $blue-title-2;
      font-style: normal;
      font-weight: 600;
    }
  }

  &__arrow {
    color: rgba(62, 103, 251, 0.55);
    font-size: 14px;
  }
}
</style>
